import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * The War Room — the module shell.
 *
 * ## Why "Ledger" is a top-level tab and not a settings page
 *
 * Because this feature publishes speech in a campaign's name, and the record of what was said
 * is not an administrative detail — it is the thing that makes the rest of it defensible. A
 * ledger buried three clicks deep is one nobody reads until a lawyer asks for it.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

export const WARROOM_TABS: Tab[] = [
  { to: '/war-room', label: 'Console', end: true,
    hint: 'The whole window as one map — subjects, then narratives, then threads' },
  { to: '/war-room/situation', label: 'Situation',
    hint: 'What is moving right now, fastest first' },
  { to: '/war-room/analytics', label: 'Analytics',
    hint: 'The window measured — pressure, narratives, reach, and the council' },
  { to: '/war-room/threads', label: 'Threads',
    hint: 'Every thread we are watching' },
  { to: '/war-room/subjects', label: 'Subjects',
    hint: 'Who we are watching — and asking the council across everything said about them' },
  { to: '/war-room/queue', label: 'Approval queue',
    hint: 'Drafts waiting for a human. Nothing posts without one' },
  { to: '/war-room/coordination', label: 'Coordination',
    hint: 'Repeated phrasing across threads — a pattern, never a person' },
  { to: '/war-room/ledger', label: 'Ledger',
    hint: 'Every approved post, who approved it, and what was sent' },
  { to: '/war-room/accounts', label: 'Accounts & voices',
    hint: 'Where we post from, and in which persona', permission: 'warroom:accounts' },
  { to: '/war-room/watches', label: 'Radar',
    hint: 'What the standing queries listen for', permission: 'warroom:watch' },
]

export function WarRoomLayout() {
  const authorities = useSession((s) => s.authorities)
  const tabs = WARROOM_TABS.filter((t) => hasPermission(authorities, t.permission))

  return (
    <div className="space-y-3">
      <nav className="axis-table-scroll flex gap-0.5 border-b border-border"
           aria-label="War room">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            title={tab.hint}
            className={({ isActive }) => cn(
              'shrink-0 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[12px]',
              isActive
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
